import React from 'react'
import { BetterComponent } from '../../../components/better-component/better-component'
import { AppState } from '../../../stores/app'
import { AccountService, DomainService, NotificationService } from '../../../services'
import { Container } from 'typescript-ioc'
import clone from 'clone'
import { JourneyNotificationsComparisonContext } from './journey-notifications-comparison-context'
import { NotificationSummaryCard, Select } from '@pushly/aqe/lib/components'
import classnames from 'classnames'
import {
    JourneyNotificationCompTabProps,
    JourneyNotificationCompTabState,
    IJourneyNotificationTableData,
    IChallengerNotificationData,
} from './types'

import './journey-notifications.scss'
import { Button, Flex, Table, Tooltip, Tag } from 'antd'
import { AntdTableEmptyPlaceholder } from '@pushly/aqe/lib/components'
import { LoadingOutlined, WifiOutlined, LockOutlined, ReloadOutlined } from '@ant-design/icons'
import { TableRowEntityDisplay } from '../../../components/table-row-entity-display/table-row-entity-display'
import { numberWithCommas, numberWithPercent } from '../../../_utils/utils'
import { InsightsService } from '../../../services/insights'
import { ImpressionsColumnView } from '../../../components/impressions-column-view/impressions-column-view'
import { ClicksColumnView } from '../../../components/clicks-column-view/clicks-column-view'
import { onDomainIdChange } from '../../../_utils/domain'
import { onAccountIdChange } from '../../../_utils/account'
import { stripUndefined } from '../../../_utils/strip-undefined'
import { extractSummaryNotification } from '../../../_utils/notifications'
import { simpleNotification } from '../../../_utils/utils'
import { ApiVersion } from '../../../enums/api-version.enum'
import titleCase from 'title-case'
import { orderSteps } from '@pushly/models/lib/structs/journeys'
import type { IJourneyStep } from '@pushly/models/lib/structs/journeys'
import { VERTICAL_WELL_GAP } from '@pushly/aqe'
import {
    buildRevisionMetaList,
    buildRevisionIdFilter,
    computeRevisionSubtotals,
    defaultScopeForReport,
    findPromotedVariantKey,
    groupRowsByRevision,
    isSingleRevisionJourney,
    isPromotedVariantRow,
    revisionDisplayLabel,
    InsightRowWithRevision,
    JourneyPerformanceReport,
    RevisionMeta,
    RevisionScope,
} from './revision-insights'
import { RevisionScopeSelect } from './revision-scope-select'
import { RevisionGroupHeader } from './revision-group-header'

type ChallengerNotificationData = IChallengerNotificationData // OrgNotificationModel | GetNotificationResponse;
type TableData = IJourneyNotificationTableData<any> // | IJourneyNotificationTableData<any>;
type IterableTableData = IJourneyNotificationTableData<any>[] // (IJourneyNotificationTableData<OrgNotificationModel> | IJourneyNotificationTableData<GetNotificationResponse>)[];

type Props = JourneyNotificationCompTabProps

type State = JourneyNotificationCompTabState & {
    view: JourneyPerformanceReport
    showCompareValues?: boolean
    compareValues?: any[]
    compareLoading?: boolean
    /** Current revision scope for the Insights tab. */
    revisionScope: RevisionScope
    /** Per-revision collapse state in the grouped (all-revisions) view, keyed by revision id. */
    __collapsedRevisions?: Record<number, boolean>
}

export class JourneyNotificationsComparisonsTab extends BetterComponent<Props, State> {
    protected static tabName = 'notifications'
    protected static tabLabel = 'Insights'

    private appState: AppState
    private domainService: DomainService
    private orgService: AccountService
    private notifSvc: NotificationService

    private insightsService: InsightsService
    private disposeObservers: any[] = []

    public constructor(props: Props) {
        super(props)

        this.appState = Container.get(AppState)
        this.domainService = Container.get(DomainService)
        this.orgService = Container.get(AccountService)
        this.notifSvc = Container.get(NotificationService)
        this.insightsService = Container.get(InsightsService)

        // Open on per-report default scope — no same-visit seed.
        const initialView: JourneyPerformanceReport = 'NOTIFICATIONS'
        const initialScope: RevisionScope = defaultScopeForReport(initialView, props.journey)

        this.state = {
            level: this.props.level,
            levelDependenciesLoaded: false,
            org: null!, // loaded on mount,
            domain: null!, // loaded on mount

            notifs: [],
            dataSourceLoaded: false,
            dataSource: [],

            sortedInfo: {
                columnKey: 'id',
                order: 'ascend',
            },
            view: initialView,

            selectedNotifications: [],
            filters: {},

            refreshing: false,
            refreshEnabled: true,

            revisionScope: initialScope,
        }
    }

    public async componentDidMount() {
        let entityIdChangeHandler = this.props.level === 'domain' ? onDomainIdChange : onAccountIdChange
        this.disposeObservers.push(
            entityIdChangeHandler(this.appState, async () => {
                await this.fetchOrganizationalDependencies()
                this.resetList()
            }),
        )

        await this.fetchOrganizationalDependencies()
        await this.fetchNotifications()
    }

    public componentWillUnmount() {
        super.componentWillUnmount()

        this.unmounting = true
        this.disposeObservers.forEach((fn) => fn())
    }

    public render() {
        const {
            levelDependenciesLoaded,
            dataSourceLoaded,
            refreshing,
            // selectedNotifications,
        } = this.state

        const showTableLoadingAnimation = !levelDependenciesLoaded || (!dataSourceLoaded && !refreshing)

        const journey = this.props.journey
        const revisionMetas = buildRevisionMetaList(journey, this.appState.currentDomain?.timezone)
        const isMultiRevision = !isSingleRevisionJourney(journey)
        const { revisionScope } = this.state
        const isGrouped = isMultiRevision && revisionScope === 'all'
        // While grouped (all-revisions), a global column sort would reorder rows across
        // revision boundaries and tear them out from under their group headers. Disable
        // column sorters in that mode; sorting is available once scoped to one revision.
        const sortable = !isGrouped

        // Build the flat data source with optional revision group-header sentinel rows injected
        const rawDataSource: IterableTableData = this.state.dataSource
        const { dataSourceWithHeaders, totalColumnCount } = this.buildGroupedDataSource(
            rawDataSource,
            revisionMetas,
            isGrouped,
        )
        const currDataSource: IterableTableData = isGrouped ? dataSourceWithHeaders : rawDataSource

        const visibleIds = rawDataSource?.map(this.getRowKey)
        const selectedVisibleIds = this.state.selectedNotifications.filter((id) => visibleIds.includes(id))

        const compareValues = this.state.compareValues ?? []
        const compareLoading = this.state.compareLoading ?? false
        const isNotificationView = this.state.view === 'NOTIFICATIONS'

        // Promoted variant key — used to stamp 🏆 on the matching variant row.
        const promotedVariantKey = findPromotedVariantKey(journey)

        // Past-revision context strip: when scoped to a specific non-active revision
        const scopedMeta = typeof revisionScope === 'number'
            ? revisionMetas.find((m) => m.id === revisionScope)
            : null
        const showPastContext = !!(scopedMeta && !scopedMeta.isActive)

        return (
            <Flex vertical gap={VERTICAL_WELL_GAP} className="journey-notifications-comparison-tab">
                {/* Report + Revision selectors side-by-side; refresh (and, for
                    Notifications, compare) controls right-aligned in the same row. */}
                <Flex align="center" gap={12} className="insights-controls-row">
                    {this.renderHeaderTitleControl()}
                    {isMultiRevision && (
                        <RevisionScopeSelect
                            revisions={revisionMetas}
                            value={revisionScope}
                            onChange={this.handleRevisionScopeChange}
                            size="middle"
                        />
                    )}
                    {this.renderInsightsControls(isNotificationView)}
                </Flex>

                {/* Past-revision context strip */}
                {showPastContext && scopedMeta && (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '10px 14px',
                            background: '#fffbe6',
                            border: '1px solid #ffe399',
                            borderRadius: 4,
                            fontSize: 13,
                        }}
                        className="insights-past-revision-context"
                    >
                        <LockOutlined />
                        <span>
                            Viewing a past revision <strong>{revisionDisplayLabel(scopedMeta)}</strong> ({scopedMeta.dateRange}).
                            This revision is read-only.
                        </span>
                    </div>
                )}

                <JourneyNotificationsComparisonContext.Provider
                    value={{
                        ...this.state,
                        onRefreshClick: this.handleRefreshClick,
                        onRefreshEnabledChange: this.handleRefreshEnabledChange,
                        onCompareClick: this.handleCompareClick,
                    }}
                >
                    <Table
                        key={`journey-performance-${this.state.view}-${revisionScope}`}
                        className={classnames('journey-notifs-list-table', 'journey-insights-table')}
                        rowClassName={(r: any) =>
                            r?.__isGroupHeader
                                ? 'revision-group-header-sentinel'
                                : r?.__isEmptyNote
                                ? 'revision-group-empty-sentinel'
                                : 'journey-notifs-list-row'
                        }
                        size="small"
                        sticky={true}
                        dataSource={currDataSource}
                        bordered={true}
                        rowKey={this.getGroupedRowKey}
                        pagination={false}
                        rowSelection={
                            isNotificationView && rawDataSource.length > 1
                                ? {
                                      // NOTE: `selections` is the select-all dropdown config, NOT the
                                      // selected set — that's `selectedRowKeys`. Don't pass row keys here.
                                      selectedRowKeys: selectedVisibleIds,
                                      hideSelectAll: true,
                                      getCheckboxProps: (r: IJourneyNotificationTableData<any>) => {
                                          const rowName = (r as any)?.data?.title
                                          const ariaLabel = rowName
                                              ? `Select ${rowName} for comparison`
                                              : 'Select row for comparison'
                                          if (this.state.selectedNotifications.length === 3) {
                                              const rowIdx = this.state.selectedNotifications.findIndex(
                                                  (v) => v === this.getRowKey(r),
                                              )
                                              return {
                                                  disabled: rowIdx === -1,
                                                  className: 'row-disabled',
                                                  'aria-label': ariaLabel,
                                              }
                                          } else return { 'aria-label': ariaLabel }
                                      },
                                      onSelect: (r: IJourneyNotificationTableData<any>) =>
                                          this.handleRowClick()(this.getRowKey(r)),
                                      // Group-header sentinel rows must not render a checkbox cell —
                                      // collapse it (colSpan 0) so the header's colSpan can span the
                                      // full data-column width starting at the first dimension column.
                                      renderCell: (_v: any, r: any, _i: number, node: React.ReactNode) =>
                                          this.isSentinelRow(r)
                                              ? { children: null, props: { colSpan: 0 } }
                                              : node,
                                  }
                                : undefined
                        }
                        onRow={(r: any) => {
                            const key = this.getRowKey(r)
                            return {
                                onClick: (_ev) => {
                                    // Exclude BOTH sentinel kinds (group headers + per-group
                                    // empty notes) — clicking an empty-note row would push an
                                    // undefined key into selectedNotifications.
                                    if (isNotificationView && !this.isSentinelRow(r)) {
                                        this.handleRowClick()(key)
                                    }
                                },
                            }
                        }}
                        locale={{
                            emptyText: (
                                <AntdTableEmptyPlaceholder text={this.emptyStateText()} icon={<WifiOutlined />} />
                            ),
                        }}
                        loading={
                            showTableLoadingAnimation && {
                                indicator: <LoadingOutlined spin={true} />,
                            }
                        }
                    >
                        {this.renderDimensionColumns(promotedVariantKey, totalColumnCount, sortable)}
                        {(this.state.view === 'STEPS' || this.state.view === 'SPLIT_TEST_VARIANTS') && (
                            <Table.Column
                                key="entrances"
                                className="entrances"
                                title="Entrants"
                                align="center"
                                dataIndex={['stats', 'entrances']}
                                width={120}
                                sorter={sortable ? (aData: any, bData: any) => this.sortStats(aData, bData, 'entrances') : undefined}
                                render={(entrances: number, row: any) =>
                                    this.isSentinelRow(row)
                                        ? this.collapsedHeaderCell()
                                        : numberWithCommas(entrances ?? 0)
                                }
                            />
                        )}
                        <Table.Column
                            key="impressions"
                            className="total-impressions"
                            dataIndex={['stats', 'impressions']}
                            title="Impressions"
                            sorter={sortable ? (aData: any, bData: any) => this.sortStats(aData, bData, 'impressions') : undefined}
                            align="center"
                            width={120}
                            render={(imps: number, row: any) => {
                                if (this.isSentinelRow(row)) return this.collapsedHeaderCell()
                                return <ImpressionsColumnView impressions={imps} />
                            }}
                        />
                        <Table.Column
                            key="clicks"
                            className="total-clicks"
                            dataIndex={['stats', 'clicks']}
                            title="Clicks"
                            align="center"
                            width={120}
                            sorter={sortable ? (aData: any, bData: any) => this.sortStats(aData, bData, 'clicks') : undefined}
                            render={(clicks: number, row: any) => {
                                if (this.isSentinelRow(row)) return this.collapsedHeaderCell()
                                return <ClicksColumnView clicks={clicks} />
                            }}
                        />
                        <Table.Column
                            key="ctr"
                            className="ctr"
                            title="CTR"
                            align="center"
                            dataIndex={['stats', 'ctr']}
                            width={120}
                            sorter={sortable ? (aData: any, bData: any) => this.sortStats(aData, bData, 'ctr') : undefined}
                            render={(ctr: number, row: any) => {
                                if (this.isSentinelRow(row)) return this.collapsedHeaderCell()
                                const impressions = row?.stats?.impressions
                                if (impressions == null || impressions <= 0) {
                                    return <div>--</div>
                                }
                                return <div>{numberWithPercent(ctr)}</div>
                            }}
                        />
                    </Table>

                    {this.state.showCompareValues && (
                        <Flex gap={8} className="journey-notifs-compare-previews">
                            {compareLoading
                                ? this.state.selectedNotifications.map((id) => (
                                      <div key={id} className="compare-card-placeholder" />
                                  ))
                                : compareValues.map((v, idx) => {
                                      const channelOptions =
                                          (v.notification?.channels ??
                                              v.notification?.template?.channels?.default?.channels) ||
                                          []
                                      return (
                                          <div
                                              key={v.notification.id}
                                              className="compare-card-wrapper"
                                          >
                                              <NotificationSummaryCard
                                                  mode="dark"
                                                  compactPreview={true}
                                                  loading={false}
                                                  notification={extractSummaryNotification(v.notification)}
                                                  channels={channelOptions}
                                                  stats={v.stats}
                                                  showScheduleDetails={false}
                                                  hideStatus={true}
                                                  isCampaignNotif={true}
                                                  domain={this.appState.currentDomain!}
                                                  hideFooter={true}
                                                  userId={this.appState.currentUser?.id}
                                              />
                                          </div>
                                      )
                                  })}
                        </Flex>
                    )}
                </JourneyNotificationsComparisonContext.Provider>
            </Flex>
        )
    }

    // ─── Revision scope handlers ─────────────────────────────────────────────

    protected handleRevisionScopeChange = async (scope: RevisionScope) => {
        // A compare selection only makes sense within one revision's data set.
        // Changing the revision scope must fully reset compare — otherwise notifications
        // picked in another revision linger in the compare tray though they aren't part
        // of the newly-scoped data.
        await this.setState({
            revisionScope: scope,
            selectedNotifications: [],
            showCompareValues: false,
            compareValues: [],
        })
        await this.fetchNotifications()
    }

    /**
     * Build a data source that interleaves revision group-header sentinel rows
     * before each group's data rows when isGrouped is true.
     *
     * Returns both the augmented data source and the column count (for colSpan in the header row).
     */
    protected buildGroupedDataSource(
        rawRows: IterableTableData,
        revisionMetas: RevisionMeta[],
        isGrouped: boolean,
    ): { dataSourceWithHeaders: IterableTableData; totalColumnCount: number } {
        // Column count: dimension cols + metric cols
        // NOTIFICATIONS: title + step + impressions + clicks + ctr = 5
        // STEPS: step + stepType + entrants + impressions + clicks + ctr = 6
        // SPLIT_TEST_VARIANTS: splitTest + variant + variantPct + entrants + impressions + clicks + ctr = 7
        const colMap: Record<string, number> = {
            NOTIFICATIONS: 5,
            STEPS: 6,
            SPLIT_TEST_VARIANTS: 7,
        }
        let totalColumnCount = colMap[this.state.view] ?? 5
        // The NOTIFICATIONS view renders an antd rowSelection (checkbox) column when there
        // are 2+ rows (see the Table's rowSelection prop). The group-header / empty-note
        // cell merges across the WHOLE row, so its colSpan must include that selection
        // column too — otherwise the merged cell is one short and the last column (CTR)
        // is left uncovered. Steps/Variants have no selection column, so they're unaffected.
        const hasSelectionColumn = this.state.view === 'NOTIFICATIONS' && rawRows.length > 1
        if (hasSelectionColumn) totalColumnCount += 1

        if (!isGrouped || revisionMetas.length === 0) {
            return { dataSourceWithHeaders: rawRows, totalColumnCount }
        }

        // No data for ANY revision → defer to the table's whole-table empty state
        // rather than rendering a header + "empty" note for every revision (which
        // reads as broken). The locale.emptyText handles this single-message case.
        if (rawRows.length === 0) {
            return { dataSourceWithHeaders: [], totalColumnCount }
        }

        // Partition the FORMATTED table rows (rawRows) by revision — using the SAME
        // null→active fallback as groupRowsByRevision — so the displayed rows and the
        // header subtotals are derived from one consistent source.
        //
        // Each formatted row already carries `__revisionId` (set in fetchNotifications
        // for all three views). groupRowsByRevision maps `__revisionId`/`revision_id`
        // onto each revision bucket; rows whose revision_id is null/unknown fall into
        // the active revision's group. This fixes the QA "all rows under one group"
        // symptom (rows with a missing revision_id were previously dropped from every
        // group because the strict `=== group.meta.id` filter never matched null).
        const groupableRows = rawRows.map((r: any) => ({
            ...r,
            // groupRowsByRevision reads `.revision_id`; map our `__revisionId` onto it.
            revision_id: r.__revisionId ?? null,
        }))
        const groups = groupRowsByRevision(groupableRows, revisionMetas)

        const dataSourceWithHeaders: any[] = []
        const collapsedMap: Record<number, boolean> = this.state.__collapsedRevisions ?? {}

        for (const group of groups) {
            // Subtotals are derived from the SAME rows shown under this header,
            // so the header summary always matches the visible data for the group.
            const subtotals = computeRevisionSubtotals(this.statsFromFormattedRows(group.rows))

            // Inject a sentinel header row
            dataSourceWithHeaders.push({
                __isGroupHeader: true,
                __revisionId: group.meta.id,
                __meta: group.meta,
                __subtotals: subtotals,
                __collapsed: collapsedMap[group.meta.id] ?? false,
                // Provide a stable key
                __key: `__header__${group.meta.id}`,
            })

            if (!collapsedMap[group.meta.id]) {
                if (group.rows.length === 0) {
                    // Revision contributed no rows for this report → explicit one-line note
                    // under the header (e.g. a linear revision in the Variants report) so the
                    // group never reads as a header floating above nothing.
                    dataSourceWithHeaders.push({
                        __isEmptyNote: true,
                        __revisionId: group.meta.id,
                        __note: this.emptyNoteForReport(),
                        __key: `__empty__${group.meta.id}`,
                    })
                } else {
                    dataSourceWithHeaders.push(...group.rows)
                }
            }
        }

        return { dataSourceWithHeaders, totalColumnCount }
    }

    /**
     * Map formatted table rows back into the InsightRowWithRevision shape so
     * computeRevisionSubtotals can aggregate when raw API stats are unavailable.
     */
    protected statsFromFormattedRows(rows: any[]): InsightRowWithRevision[] {
        return rows.map((r) => ({
            revision_id: r.__revisionId ?? null,
            entrances: r.stats?.entrances ?? 0,
            impressions: r.stats?.impressions ?? 0,
            clicks: r.stats?.clicks ?? 0,
            ctr_decimal: r.stats?.ctr ?? null,
        }))
    }

    /**
     * Render the RevisionGroupHeader via antd's colSpan cell-merging pattern.
     * Returned from the FIRST (dimension) column's `render` for a sentinel row —
     * it spans `totalColumnCount` data columns. The row record is read directly
     * from the column render args (which antd always provides).
     */
    protected renderGroupHeaderCell = (row: any, totalColumnCount: number) => {
        return {
            children: (
                <RevisionGroupHeader
                    meta={row.__meta}
                    subtotals={row.__subtotals}
                    collapsed={row.__collapsed}
                    onToggle={() => this.handleToggleRevisionGroup(row.__revisionId)}
                    colSpan={totalColumnCount}
                    report={this.state.view}
                />
            ),
            props: { colSpan: totalColumnCount },
        }
    }

    /** Collapsed (merged-away) cell for non-first columns on a group-header / empty-note row. */
    protected collapsedHeaderCell() {
        return { children: null, props: { colSpan: 0 } }
    }

    /** True for full-width sentinel rows (group headers + per-group empty notes). */
    protected isSentinelRow(row: any): boolean {
        return !!(row?.__isGroupHeader || row?.__isEmptyNote)
    }

    /**
     * The one-line note shown UNDER a revision group header when that revision
     * contributed no rows for the current report (so a header never floats above
     * nothing). Report-aware copy across all three reports.
     */
    protected emptyNoteForReport(): string {
        switch (this.state.view) {
            case 'SPLIT_TEST_VARIANTS':
                return 'No split test in this revision — the path is linear.'
            case 'STEPS':
                return 'No steps recorded for this revision.'
            default:
                return 'No notifications recorded for this revision.'
        }
    }

    /**
     * Copy for the whole-table empty state (no data for any revision). Report-aware
     * so the Variants report explains why the report is blank.
     */
    protected emptyStateText(): string {
        if (this.state.view === 'SPLIT_TEST_VARIANTS') {
            return 'This journey has no split tests. Split Test Variants appears once a split-test step has collected data.'
        }
        return 'No data for this report yet.'
    }

    /**
     * Render the per-group empty-note sentinel as a full-width merged cell — same
     * colSpan pattern as the group header (returned from the first dimension column's render).
     */
    protected renderEmptyNoteCell = (row: any, totalColumnCount: number) => {
        return {
            children: (
                <div className="revision-group-empty-note">
                    <span className="rge-dash">—</span>
                    <span>{row.__note}</span>
                </div>
            ),
            props: { colSpan: totalColumnCount },
        }
    }

    protected handleToggleRevisionGroup = async (revisionId: number) => {
        const prev: Record<number, boolean> = this.state.__collapsedRevisions ?? {}
        await this.setState({
            __collapsedRevisions: { ...prev, [revisionId]: !prev[revisionId] },
        })
    }

    /**
     * Row key for the grouped data source — handles both real rows and sentinel header rows.
     */
    protected getGroupedRowKey = (row: any) => {
        if (row?.__isGroupHeader || row?.__isEmptyNote) return row.__key
        return this.getRowKey(row)
    }

    protected async fetchNotifications() {
        // start loading
        await this.setState({
            dataSourceLoaded: false,
        })

        const state: any = {
            ...this.state,
            refreshing: false,
            dataSourceLoaded: true,
        }

        const insightsPackage = this.buildInsightsPackageForView()

        if (this.props.level === 'domain') {
            insightsPackage.action_attribution = 'send_time'
        }

        const stats = await this.insightsService.fetch(insightsPackage, {
            requestScope: this.props.level === 'org' ? 'organizations' : 'domains',
            scopeId: this.props.level === 'org' ? this.props.orgId : this.props.domainId,
            // Campaign-entity insights must hit the generic scoped insights route (no nested scope).
            // Notification view continues to use /notifications/insights for parity with existing behavior.
            nestedScope: this.state.view === 'NOTIFICATIONS' ? 'notifications' : undefined,
            cancellationKey: 'cnpl.stats',
        })

        // Stash the raw stats (with revision_id) for client-side grouping.
        state.__rawStats = stats ?? []

        if (this.state.view === 'NOTIFICATIONS') {
            state.dataSource = stats.map((stat) => ({
                // Carry revision_id on the row for group-by-revision mapping
                __revisionId: stat.revision_id ?? null,
                data: {
                    ...stat.notification,
                    step: stat.step,
                },
                stats: {
                    impressions: stat.impressions,
                    clicks: stat.clicks,
                    ctr: stat.ctr_decimal,
                },
            }))
        } else if (this.state.view === 'STEPS') {
            const stepRows = stats.map((stat) => ({
                __revisionId: stat.revision_id ?? null,
                data: {
                    step: stat.step || {},
                    split_test: stat.split_test || {},
                },
                stats: {
                    entrances: stat.entrances || 0,
                    impressions: stat.impressions,
                    clicks: stat.clicks,
                    ctr: stat.ctr_decimal,
                },
            }))
            state.dataSource = this.sortStepRowsByWorkflowOrder(stepRows)
        } else {
            state.dataSource = stats.map((stat, idx) => ({
                __revisionId: stat.revision_id ?? null,
                // The warehouse `variant.id` collapses to 0 for every variant row, so it
                // can't be a React key — colliding keys broke antd reconciliation (a
                // collapsed revision group only shed some of its rows). Assign a stable,
                // unique per-row key at fetch time; it travels with the row through
                // sort/collapse (rows aren't refetched on those) so it stays stable.
                __rowKey: `variant:${idx}:${stat.revision_id ?? 'x'}`,
                data: {
                    split_test: stat.split_test || {},
                    variant: stat.variant || {},
                },
                stats: {
                    entrances: stat.entrances || 0,
                    impressions: stat.impressions,
                    clicks: stat.clicks,
                    ctr: stat.ctr_decimal,
                },
            }))
        }

        await this.setState(state)
    }

    protected buildWorkflowStepOrderMap(): Map<number, number> {
        const steps = (this.props.journey?.revision?.steps || []) as IJourneyStep[]
        const orderedSteps = orderSteps(steps)
        const orderMap = new Map<number, number>()

        orderedSteps.forEach((step, idx) => {
            const stepId = Number((step as any)?.id)
            if (!Number.isNaN(stepId)) {
                orderMap.set(stepId, idx)
            }
        })

        return orderMap
    }

    protected sortStepRowsByWorkflowOrder(rows: TableData[]): TableData[] {
        const orderMap = this.buildWorkflowStepOrderMap()

        return [...rows].sort((a, b) => {
            const aStepId = Number(a?.data?.step?.id)
            const bStepId = Number(b?.data?.step?.id)

            const aOrder = orderMap.get(aStepId)
            const bOrder = orderMap.get(bStepId)

            if (aOrder != null && bOrder != null) return aOrder - bOrder
            if (aOrder != null) return -1
            if (bOrder != null) return 1

            if (!Number.isNaN(aStepId) && !Number.isNaN(bStepId)) return aStepId - bStepId
            if (!Number.isNaN(aStepId)) return -1
            if (!Number.isNaN(bStepId)) return 1
            return 0
        })
    }

    protected buildInsightsPackageForView(): any {
        const baseFilters: any[] = [
            {
                field: 'domain.id',
                operator: 'in',
                value: [this.appState.currentDomain!.id],
            },
            {
                field: 'campaign.id',
                operator: 'IN',
                value: [this.props.journey.id],
            },
        ]

        // When scoped to a specific revision, add the revision_id filter.
        // Filter channel: generic `filters` array with field='revision_id'.
        // challenger's MeasureFieldProcessor accepts this via resolveRevisionIdFilter().
        const revisionScope = this.state.revisionScope
        if (typeof revisionScope === 'number') {
            baseFilters.push(buildRevisionIdFilter(revisionScope))
        }

        if (this.state.view === 'NOTIFICATIONS') {
            // challenger supports revision_id on the notification breakdown; include
            // it in fields and honor the revision filter — mirrors STEPS.
            return {
                date_preset: 'lifetime',
                entity: 'notifications',
                date_increment: 'lifetime',
                breakdowns: ['notification'],
                fields: [
                    'campaign.id',
                    'notification.id',
                    'notification.title',
                    'notification.body',
                    'step.id',
                    'step.name',
                    'impressions',
                    'clicks',
                    'ctr_decimal',
                    'notification.source',
                    // revision identity for client-side grouping (mirrors STEPS)
                    'revision_id',
                ],
                filters: baseFilters,
            }
        }

        if (this.state.view === 'STEPS') {
            return {
                date_preset: 'lifetime',
                entity: 'campaigns',
                date_increment: 'lifetime',
                breakdowns: ['campaign', 'step'],
                fields: [
                    'campaign.id',
                    'step.id',
                    'step.name',
                    'step.type',
                    'entrances',
                    'impressions',
                    'clicks',
                    'ctr_decimal',
                    // revision identity for client-side grouping
                    'revision_id',
                ],
                filters: baseFilters,
            }
        }

        return {
            date_preset: 'lifetime',
            entity: 'campaigns',
            date_increment: 'lifetime',
            breakdowns: ['campaign', 'variant'],
            fields: [
                'campaign.id',
                'split_test.name',
                'variant.id',
                'variant.name',
                'variant.percentage',
                'entrances',
                'impressions',
                'clicks',
                'ctr_decimal',
                // revision identity for client-side grouping + promoted variant lookup
                'revision_id',
                // purchases for conditional purchase columns
                'purchases',
                'purchase_amount',
                'purchase_rate',
            ],
            filters: baseFilters,
        }
    }

    protected async fetchOrganizationalDependencies() {
        let state: any = { levelDependenciesLoaded: false }
        await this.setState({ ...state })

        if (this.props.level === 'domain') {
            const { ok, data } = await this.domainService.fetchById(this.props.domainId)
            if (ok && data) {
                state.domain = data
            }

            state.level = 'domain'
        } else {
            state.org = await this.orgService.fetchById(this.props.orgId)
        }

        state.levelDependenciesLoaded = true
        await this.setState(state)
    }

    protected async resetList() {
        await this.setState({
            refreshing: true,
            selectedNotifications: [],
            showCompareValues: false,
        })

        await this.fetchNotifications()
    }

    protected getRowKey = (row: TableData) => {
        // Guard for group-header/sentinel rows — they have no .data and no .id.
        // Return the pre-computed stable __key to prevent a crash.
        if ((row as any)?.__isGroupHeader) return (row as any).__key ?? `__header__unknown`
        if ((row as any)?.__isEmptyNote) return (row as any).__key ?? `__empty__unknown`
        if (this.state.view === 'STEPS') return row.data?.step?.id
        // Variant rows can't key off variant.id (warehouse collapses it to 0 for every
        // row → key collisions). Use the stable per-row key assigned in fetchNotifications.
        if (this.state.view === 'SPLIT_TEST_VARIANTS') return (row as any).__rowKey ?? row.data?.variant?.id
        return row.data?.id
    }

    protected sortStats(aRow: any, bRow: any, stat: string) {
        // Sentinel rows (group headers / per-group empty notes) carry no `.stats` —
        // keep them anchored instead of dereferencing undefined (the crash in the QA
        // report). Sorters are also disabled while grouped (see `sortable` in render),
        // so antd won't normally pass a sentinel here; this stays defensive.
        if (this.isSentinelRow(aRow) || this.isSentinelRow(bRow)) return 0
        const aStat = aRow?.stats?.[stat] ?? 0
        const bStat = bRow?.stats?.[stat] ?? 0

        return aStat > bStat ? 1 : bStat > aStat ? -1 : 0
    }

    protected handleRowClick = (ev?: any) => (key) => {
        let currSelection = [...this.state.selectedNotifications]
        if (currSelection.includes(key)) {
            currSelection = currSelection.filter((id) => id !== key)
        } else {
            currSelection.push(key)
        }

        if (currSelection.length > 3) {
            return
        }

        this.setState({ selectedNotifications: currSelection })
    }

    protected handleRefreshClick = async (_ev: React.MouseEvent<HTMLElement>): Promise<void> => {
        await this.resetList()
    }

    protected handleRefreshEnabledChange = async (refreshEnabled: boolean) => {
        await this.setState({ refreshEnabled })
    }

    protected handleViewChange = async (view: JourneyPerformanceReport) => {
        if (!view || view === this.state.view) return

        // Report type and revision scope are orthogonal — persist the user's
        // selected revision across a report-type switch. Only fall back to the report's
        // default scope when the current scope isn't valid for the new report (i.e. a
        // stale revision id no longer present; 'all' and any existing revision are valid
        // for every report).
        const metas = buildRevisionMetaList(this.props.journey, this.appState.currentDomain?.timezone)
        const current = this.state.revisionScope
        const scopeStillValid = current === 'all' || metas.some((m) => m.id === current)
        const newScope = scopeStillValid ? current : defaultScopeForReport(view, this.props.journey)

        await this.setState({
            view,
            // Compare is per-report (notification rows differ by report), so still reset it.
            selectedNotifications: [],
            showCompareValues: false,
            compareValues: [],
            revisionScope: newScope,
        })

        await this.fetchNotifications()
    }

    protected handleCompareClick = async () => {
        await this.setState({ showCompareValues: true, compareLoading: true })

        let compareValues: TableData[] = []
        const { filters } = this.state

        const compareIds = this.state.selectedNotifications
        const data = clone(this.state.dataSource)

        compareValues = data.filter((datum) => {
            const match = compareIds.includes(this.getRowKey(datum))
            return match && datum.data
        })

        const baseApiOptions = {
            showLoadingScreen: false,
            query: stripUndefined({
                include_segments: true,
                include_schedules: false,
                search: filters.search ?? undefined,
            }),
            version: ApiVersion.V4,
        }

        try {
            let resolvers: Array<Promise<any>> = []
            if (Array.isArray(compareValues) && compareValues.length > 0) {
                compareValues.map((val) => {
                    const notificationId = val.data.id
                    const apiOptions = {
                        ...baseApiOptions,
                        cancellationKey: `journey-notif-list.fetch-${notificationId}`,
                    }

                    // fetch notifications
                    resolvers.push(
                        this.notifSvc.fetchNotificationById(
                            this.appState.currentDomain!.id,
                            notificationId,
                            apiOptions,
                        ),
                    )
                })
            }

            const notifications = await Promise.all(resolvers)

            compareValues = compareValues.map((val) => {
                const notification = notifications.find((notif) => notif?.id === val.data.id)
                return {
                    ...val,
                    notification,
                }
            })

            this.setState({ compareValues, compareLoading: false })
        } catch (error) {
            this.setState({ compareLoading: false })
            simpleNotification('error', 'Failed to load notification data for comparison. Please try again.')
        }
    }

    protected renderNotificationDisplay = (data: any) => {
        return (
            <div className="notification-display-data">
                <TableRowEntityDisplay title={data.title} />
            </div>
        )
    }

    protected formatStepType(stepType?: string): string {
        if (!stepType) return '—'

        const [baseType, configType] = String(stepType).split(':')
        const prettify = (value: string) => titleCase(value.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim())

        return configType ? `${prettify(baseType)}: ${prettify(configType)}` : prettify(baseType)
    }

    protected renderHeaderTitleControl = () => {
        const options = [
            { value: 'NOTIFICATIONS', label: 'Notifications' },
            { value: 'STEPS', label: 'Steps' },
            { value: 'SPLIT_TEST_VARIANTS', label: 'Split Test Variants' },
        ]

        return (
            <div className="journey-notifs-title-selector">
                <Select
                    size="middle"
                    className="journey-notifs-title-select"
                    prefix="Report:"
                    showSearch={false}
                    value={[this.state.view]}
                    onChange={this.handleViewChange}
                    options={options}
                    style={{ width: 260 }}
                />
            </div>
        )
    }

    /**
     * Refresh + compare controls in the insights controls row (right-aligned).
     * Compare is disabled until 2+ rows are selected and only shown for the
     * Notifications report; refresh reflects the refreshing/refreshEnabled state.
     */
    protected renderInsightsControls = (isNotificationView: boolean) => {
        const compareDisabled = this.state.selectedNotifications.length < 2

        return (
            <Flex align="center" gap={8} className="insights-controls-actions" style={{ marginLeft: 'auto' }}>
                {isNotificationView && (
                    <Button
                        className="insights-compare-btn"
                        disabled={compareDisabled}
                        size="middle"
                        onClick={this.handleCompareClick}
                    >
                        Compare
                    </Button>
                )}
                <Tooltip title="Refresh">
                    <Button
                        className="insights-refresh-btn journey-notifs-list-refresh-toggle"
                        aria-label="Refresh"
                        disabled={!this.state.refreshEnabled}
                        size="middle"
                        icon={<ReloadOutlined spin={this.state.refreshing} />}
                        onClick={this.handleRefreshClick}
                    />
                </Tooltip>
            </Flex>
        )
    }

    protected renderDimensionColumns = (
        promotedVariantKey?: string | null,
        totalColumnCount: number = 5,
        sortable: boolean = true,
    ) => {
        if (this.state.view === 'STEPS') {
            return (
                <>
                    <Table.Column
                        key="step-name"
                        className="step"
                        dataIndex={['data', 'step']}
                        title="Step"
                        width={260}
                        align="left"
                        ellipsis={{ showTitle: true }}
                        render={(step: any, row: any) =>
                            row?.__isGroupHeader
                                ? this.renderGroupHeaderCell(row, totalColumnCount)
                                : row?.__isEmptyNote
                                ? this.renderEmptyNoteCell(row, totalColumnCount)
                                : (step?.name || '—')
                        }
                    />
                    <Table.Column
                        key="step-type"
                        className="step-type"
                        dataIndex={['data', 'step']}
                        title="Step Type"
                        width={220}
                        align="left"
                        ellipsis={{ showTitle: true }}
                        render={(step: any, row: any) =>
                            this.isSentinelRow(row) ? this.collapsedHeaderCell() : this.formatStepType(step?.type)
                        }
                    />
                </>
            )
        }

        if (this.state.view === 'SPLIT_TEST_VARIANTS') {
            return (
                <>
                    <Table.Column
                        key="split-test"
                        className="split-test"
                        dataIndex={['data', 'split_test']}
                        title="Split Test"
                        width={220}
                        align="left"
                        ellipsis={{ showTitle: true }}
                        render={(splitTest: any, row: any) =>
                            row?.__isGroupHeader
                                ? this.renderGroupHeaderCell(row, totalColumnCount)
                                : row?.__isEmptyNote
                                ? this.renderEmptyNoteCell(row, totalColumnCount)
                                : (splitTest?.name || '—')
                        }
                    />
                    <Table.Column
                        key="variant-name"
                        className="variant-name"
                        dataIndex={['data', 'variant']}
                        title="Variant"
                        width={200}
                        align="left"
                        ellipsis={{ showTitle: true }}
                        render={(variant: any, row: any) => {
                            if (this.isSentinelRow(row)) return this.collapsedHeaderCell()
                            const name = variant?.name || '—'
                            // 🏆 Promoted badge — strictly on the promoted variant row only.
                            // Derived from promoted_variant_key in the journey config (not a "leader" cue).
                            const isPromoted = isPromotedVariantRow(
                                { variant, revision_id: row?.__revisionId },
                                promotedVariantKey ?? null,
                            )
                            return (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <span style={{ fontWeight: isPromoted ? 700 : 400 }}>{name}</span>
                                    {isPromoted && (
                                        <Tag
                                            style={{
                                                fontSize: 10,
                                                fontWeight: 700,
                                                letterSpacing: '0.04em',
                                                textTransform: 'uppercase',
                                                color: '#722ed1',
                                                background: '#f3ebfb',
                                                borderRadius: 2,
                                                padding: '1px 6px',
                                                border: 'none',
                                            }}
                                        >
                                            🏆 Promoted
                                        </Tag>
                                    )}
                                </span>
                            )
                        }}
                    />
                    <Table.Column
                        key="variant-pct"
                        className="variant-pct"
                        dataIndex={['data', 'variant']}
                        title="Variant %"
                        width={120}
                        align="center"
                        sorter={
                            sortable
                                ? (aData: any, bData: any) =>
                                      (aData?.data?.variant?.percentage || 0) - (bData?.data?.variant?.percentage || 0)
                                : undefined
                        }
                        render={(variant: any, row: any) =>
                            this.isSentinelRow(row)
                                ? this.collapsedHeaderCell()
                                : variant?.percentage != null
                                ? numberWithPercent(variant.percentage / 100)
                                : '—'
                        }
                    />
                </>
            )
        }

        return (
            <>
                <Table.Column
                    key="id"
                    defaultSortOrder="ascend"
                    className="title"
                    dataIndex={['data']}
                    title="Title"
                    width={300}
                    render={(data: any, row: any) =>
                        row?.__isGroupHeader
                            ? this.renderGroupHeaderCell(row, totalColumnCount)
                            : row?.__isEmptyNote
                            ? this.renderEmptyNoteCell(row, totalColumnCount)
                            : this.renderNotificationDisplay(data)
                    }
                />
                <Table.Column
                    key="step"
                    className="step"
                    dataIndex={['data', 'step']}
                    title="Step"
                    width={250}
                    align="left"
                    ellipsis={{ showTitle: true }}
                    render={(step: any, row: any) =>
                        this.isSentinelRow(row) ? this.collapsedHeaderCell() : (step?.name || '—')
                    }
                />
            </>
        )
    }

}
